import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import axios from 'axios';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import {
  makeStyles,
  createStyles,
} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { setData } from '../../../actions/index';
import nl2br from 'react-nl2br';

const useStyles = makeStyles(theme => createStyles({
  root: {
    marginBottom: 50,
  },
  wrapper: {
    padding: 20,
  },
  header: {
    display: 'flex',
  },
  headerHeading: {
    flexGrow: 1,
    '& h4': {
      margin: 0,
    },
    '& p': {
      fontWeight: 'normal',
      fontSize: '0.8em',
      display: 'inline-block',
      margin: 0,
    },
  },
  surveyHeading: {
    fontSize: isMobileApp
      ? '1.4em'
      : '1.8em',
  },
  questionHeading: {
    fontSize: isMobileApp
      ? '1.2em'
      : '1.6em',
  },
  questionHeadingNumber: {
    fontWeight: 'normal',
    fontSize: isMobileApp
      ? '0.6em'
      : '0.8em',
    textTransform: 'uppercase',
    color: '#5F6C85',
  },
  questionHeadingSubText: {
    fontWeight: 'normal',
    fontSize: '0.6em',
    color: '#5F6C85',
  },
  answersWrapper: {
    backgroundColor: '#F6F6F8',
    padding: isMobileApp
      ? 10
      : 20,
    marginBottom: isMobileApp
      ? 20
      : 40,
  },
  answers: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  answer: {
    flex: '1 1 0px',
    textAlign: 'center',
    padding: 5,
  },
  answerButton: {
    width: isMobileApp
      ? 28
      : 56,
    height: isMobileApp
      ? 28
      : 56,
    backgroundColor: '#FFFFFF',
    border: '2px solid #0a72c7',
    borderRadius: '50%',
    fontWeight: 'bold',
    lineHeight: 1.9,
    fontSize: isMobileApp
      ? 16
      : 28,
    display: 'block',
    margin: '0 auto',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#0a72c7',
      color: '#ffffff',
    },
  },
  answerButtonSelected: {
    backgroundColor: '#0a72c7',
    color: '#ffffff',
  },
  answerLabel: {
    color: '#43526F',
    fontSize: '14px',
    marginTop: 10,
  },
  footer: {
    marginTop: 20,
    display: 'flex',
  },
  flex: {
    display: 'flex',
  },
  flexGrow: {
    flexGrow: 1,
  },
  paddingHorizontal: {
    padding: '0 10px',
  },
}));

const possibleAnswers = [ ...Array(11).keys() ];

export default function Survey() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(undefined, { useSuspense: false });
  const source = axios.CancelToken.source();
  const user = useSelector(state => state.user);
  const [survey, setSurvey] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [answerComment, setAnwserComment] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [question, setQuestion] = useState(null);
  const [showPrevQuestionButton, setShowPrevQuestionButton] = useState(false);

  const onPreviousQuestionClick = () => {
    prepareQuestion(questionIndex - 1);
  };

  const onSkipQuestionClick = () => {
    onNextQuestionClick(true);
  };

  const onNextQuestionClick = (isSkip = false) => {
    // This button now acts as dismiss, because we are out of questions
    if (!question) {
      onSurveyDismissClick();

      return;
    }

    // No need of useCallback here, because that method
    // will be different for basically every change.
    if (
      !isSkip &&
      answer === null
    ) {
      enqueueSnackbar(t('Please select an answer first'), {
        variant: 'error',
        autoHideDuration: 2000,
      });

      return;
    }

    const formData = {
      survey_id: survey.survey_id,
      question_id: question.id,
      answer,
      comment: answerComment,
      finalise: questionIndex + 1 >= survey.questions.length ? 'yes' : 'no',
    };

    axios
      .post(`${window.apiUrl}/answersurvey`, qs.stringify(formData), {
        cancelToken: source.token,
      })
      .then((response) => {
        if (response.data.error) {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });

          return;
        }

        // Persist this values to our local survey variable,
        // in case the user goes back.
        survey.questions[questionIndex].answer = answer;
        survey.questions[questionIndex].answer_comment = answerComment;

        setSurvey(survey);
        prepareQuestion(questionIndex + 1);
      })
      .catch(() => {})
    ;
  };

  const onSurveyDismissClick = useCallback(() => {
    setSurvey(null);

    user.survey_id = null;
    dispatch(setData(user, 'user'))
  }, [answer, questionIndex]);

  const prepareQuestion = (newQuestionIndex) => {
    if (!survey) {
      return false;
    }

    if (newQuestionIndex <= 0) {
      newQuestionIndex = 0;
      setShowPrevQuestionButton(false);
    } else {
      setShowPrevQuestionButton(true);
    }

    const newQuestion = survey.questions[newQuestionIndex];

    setQuestion(newQuestion);
    setQuestionIndex(newQuestionIndex);

    // We are done with the questions!
    if (!newQuestion) {
      setShowPrevQuestionButton(false);

      return false;
    }

    setAnswer(newQuestion.answer);
    setAnwserComment(newQuestion.answer_comment);

    return true;
  };

  useEffect(() => {
    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    if (!user.survey_id) {
      return;
    }

    axios
      .get(`${window.apiUrl}/survey`, {
        cancelToken: source.token,
      })
      .then((response) => {
        if (
          !response.data.success ||
          !response.data.data.has_survey
        ) {
          return;
        }

        setSurvey(response.data.data);
      })
      .catch(() => {})
    ;
  }, [user.survey_id]);

  useEffect(() => {
    if (!survey) {
      return;
    }

    prepareQuestion(0);
  }, [survey]);

  if (
    !survey ||
    survey.questions.length === 0
  ) {
    return null;
  }

  return (
    <div className={`wellbeing-survey ${classes.root}`}>
      <Paper className={classes.wrapper} elevation={3}>
        <Grid className={classes.header}>
          <Grid className={classes.headerHeading}>
            <h4>{t('Take the latest wellbeing survey')}</h4>
            <p>({t('This will be shared with the school')})</p>
          </Grid>
          <Grid>
            <IconButton
              onClick={onSurveyDismissClick}
              className="wellbeing-survey-close-button"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <div className={classes.content}>
          <h3 className={classes.surveyHeading}>{survey.title}</h3>
          {!question && (
            <>
              <h3 className={classes.questionHeading}>
                <div className={classes.questionHeadingText}>
                  {t('Thanks for completing our survey')}
                </div>
              </h3>
              <p>{nl2br(survey.description_after)}</p>
            </>
          )}
          {question && (
            <>
              <h3 className={classes.questionHeading}>
                <div className={classes.questionHeadingNumber}>
                  {t('Question')} {question.number}
                </div>
                <div className={classes.questionHeadingText}>
                  {question.question}
                </div>
                <div className={classes.questionHeadingSubText}>
                  {t('Choose a number between 0 and 10')}
                </div>
              </h3>
              <div className={classes.answersWrapper}>
                <div className={classes.answers}>
                  {possibleAnswers.map((possibleAnswer) => {
                    const answerButtonClassName = possibleAnswer === answer
                      ? `${classes.answerButton} ${classes.answerButtonSelected}`
                      : classes.answerButton;

                    return (
                      <div
                        key={possibleAnswer}
                        className={`answer-${possibleAnswer} ${classes.answer}`}
                        onClick={() => {
                          setAnswer(possibleAnswer);
                        }}
                      >
                        <div className={answerButtonClassName}>{possibleAnswer}</div>
                      </div>
                    );
                  })}
                </div>
                <div className={`${classes.flex} ${classes.paddingHorizontal}`}>
                  <div className={classes.flexGrow}>
                    <div className={classes.answerLabel}>
                      {isMobileApp && (
                        <span>0 </span>
                      )}
                      {t('Not at all')}
                    </div>
                  </div>
                  <div>
                    <div className={classes.answerLabel}>
                      {isMobileApp && (
                        <span>10 </span>
                      )}
                      {t('Absolutely')}
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.answerCommentWrapper}>
                <p className={classes.answerCommentText}>
                  {t('What makes you feel this way')}? ({t('optional')})
                </p>
                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  value={answerComment}
                  onChange={(event) => {
                    setAnwserComment(event.target.value);
                  }}
                  className="answer-comment-textfield"
                />
              </div>
            </>
          )}
        </div>
        <Grid className={classes.footer}>
          <Grid>
            {showPrevQuestionButton && (
               <Button
                size="small"
                onClick={onPreviousQuestionClick}
                className="previous-question-button"
              >
                &#60; {t('Previous question')}
              </Button>
            )}
          </Grid>
          <Grid className={classes.flexGrow}>
            {question && (
              <Button
                size="small"
                onClick={onSkipQuestionClick}
                className="skip-question-button"
              >
                {t('Skip question')} &#62;
              </Button>
            )}
          </Grid>
          <Grid>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={onNextQuestionClick}
              disabled={question && answer === null}
              className="next-question-button"
            >
              {!question && (
                <>{t('Close survey')}</>
              )}
              {question && (
                <>{t('Next')} &#62;</>
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
